import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import EditorJS, { OutputData } from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
// @ts-ignore
import Table from '@editorjs/table';
import InlineCode from '@editorjs/inline-code';
import Marker from '@editorjs/marker';
import { cn, signImageUrlsInHtml } from '@/lib/utils';
import { htmlToEditorJS, editorJSToHtml, isEditorJSData } from '@/lib/editorjs-utils';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { SimpleImageTool } from '@/lib/SimpleImageTool';
import { useToast } from '@/hooks/use-toast';

export interface EditorJSBlockData {
  type: string;
  data: Record<string, unknown>;
}

interface WYSIWYGEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
  phaseIndex?: number;
  stepIndex?: number;
  onMoveBlockOut?: (direction: 'up' | 'down', block: EditorJSBlockData, remainingHtml: string) => void;
  onCreateStepFromSelection?: (selectedContent: string) => void;
}

type SelectionCapture =
  | { mode: 'partial'; range: Range; blockIndex: number }
  | { mode: 'blocks'; blockIndices: number[] };

const CONVERT_TO_STEP_ICON = '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>';

class ConvertToStepTune {
  private api: any;
  private config: { handlerRef: React.MutableRefObject<((blockIndex: number) => void) | null> };

  static get isTune() { return true; }

  constructor({ api, config }: { api: any; config: any }) {
    this.api = api;
    this.config = config;
  }

  render() {
    return {
      icon: CONVERT_TO_STEP_ICON,
      title: 'Convertir en nouvelle étape',
      onActivate: () => {
        const blockIndex = this.api.blocks.getCurrentBlockIndex();
        this.config.handlerRef?.current?.(blockIndex);
      },
    };
  }

  save() { return undefined; }
}

export function WYSIWYGEditor({ value, onChange, placeholder = "Tapez votre instruction...", className, readOnly = false, phaseIndex, stepIndex, onMoveBlockOut, onCreateStepFromSelection }: WYSIWYGEditorProps) {
  const editorRef = useRef<EditorJS | null>(null);
  const holderRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const { setCurrentPhase, setCurrentStep } = useWorkflow();
  const createStepCallbackRef = useRef(onCreateStepFromSelection);
  const captureRef = useRef<SelectionCapture | null>(null);
  const convertHandlerRef = useRef<((blockIndex: number) => void) | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const shouldRenderStatic = readOnly;
  const [signedStatic, setSignedStatic] = useState<string>("");

  const handleEditorFocus = () => {
    if (phaseIndex !== undefined && stepIndex !== undefined) {
      setCurrentPhase(phaseIndex);
      setCurrentStep(stepIndex);
    }
  };

  useEffect(() => {
    createStepCallbackRef.current = onCreateStepFromSelection;
  }, [onCreateStepFromSelection]);

  useEffect(() => {
    if (!onCreateStepFromSelection) {
      convertHandlerRef.current = null;
      return;
    }
    convertHandlerRef.current = async (blockIndex: number) => {
      const editor = editorRef.current;
      if (!editor) return;
      try {
        const outputData = await editor.save();
        if (blockIndex < 0 || blockIndex >= outputData.blocks.length) return;
        const block = outputData.blocks[blockIndex];
        const extractedHtml = editorJSToHtml({ ...outputData, blocks: [block] });
        if (!extractedHtml.trim()) throw new Error('Empty content');
        editor.blocks.delete(blockIndex);
        const remaining = await editor.save();
        const remainingHtml = editorJSToHtml(remaining);
        setCurrentValue(remainingHtml);
        onChange(remainingHtml);
        createStepCallbackRef.current?.(extractedHtml);
      } catch {
        toast({
          title: "Conversion échouée",
          description: "Le contenu n'a pas pu être déplacé vers une nouvelle étape.",
          variant: "destructive",
        });
      }
    };
  }, [onCreateStepFromSelection, onChange, toast]);

  // --- Context menu: selection capture and action ---

  const getBlockElements = (): HTMLElement[] => {
    const container = holderRef.current;
    if (!container) return [];
    return Array.from(container.querySelectorAll('.ce-block'));
  };

  const findBlockIndex = (node: Node | null): number => {
    if (!node) return -1;
    const el = node instanceof HTMLElement ? node : node.parentElement;
    if (!el) return -1;
    const blockEl = el.closest('.ce-block');
    if (!blockEl) return -1;
    return getBlockElements().indexOf(blockEl as HTMLElement);
  };

  const menuPosition = (x: number, y: number) => {
    const w = 260, h = 46, pad = 8;
    return {
      x: Math.max(pad, Math.min(x, window.innerWidth - w - pad)),
      y: Math.max(pad, Math.min(y, window.innerHeight - h - pad)),
    };
  };

  const handleContextMenu = useCallback((e: MouseEvent) => {
    if (!onCreateStepFromSelection || !holderRef.current) return;
    const container = holderRef.current;
    captureRef.current = null;

    const allBlocks = getBlockElements();

    // 1) EditorJS marks cross-block selections with .ce-block--selected.
    //    This is the ONLY reliable way to detect multi-block selection.
    const selectedBlockEls = allBlocks.filter(el => el.classList.contains('ce-block--selected'));
    if (selectedBlockEls.length > 0) {
      const indices = selectedBlockEls.map(el => allBlocks.indexOf(el)).filter(i => i >= 0);
      if (indices.length > 0) {
        captureRef.current = { mode: 'blocks', blockIndices: indices };
        e.preventDefault();
        e.stopImmediatePropagation();
        setContextMenu(menuPosition(e.clientX, e.clientY));
        return;
      }
    }

    // 2) Single-block text selection via native browser selection.
    //    Reliable because it is within ONE contenteditable element.
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed
        && sel.anchorNode && container.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0);
      const blockIdx = findBlockIndex(sel.anchorNode);
      if (blockIdx >= 0) {
        captureRef.current = { mode: 'partial', range: range.cloneRange(), blockIndex: blockIdx };
        e.preventDefault();
        e.stopImmediatePropagation();
        setContextMenu(menuPosition(e.clientX, e.clientY));
        return;
      }
    }

    // 3) No selection at all: right-click on a block (image, table, etc.).
    const target = e.target as HTMLElement | null;
    const blockEl = target?.closest('.ce-block');
    if (blockEl && container.contains(blockEl)) {
      const idx = allBlocks.indexOf(blockEl as HTMLElement);
      if (idx >= 0) {
        captureRef.current = { mode: 'blocks', blockIndices: [idx] };
        e.preventDefault();
        e.stopImmediatePropagation();
        setContextMenu(menuPosition(e.clientX, e.clientY));
      }
    }
  }, [onCreateStepFromSelection]);

  const handleContextMenuAction = useCallback(async () => {
    if (!contextMenu || isProcessing || !editorRef.current) return;
    const capture = captureRef.current;
    if (!capture) return;

    setIsProcessing(true);
    setContextMenu(null);

    try {
      const editor = editorRef.current;
      let extractedHtml = '';

      if (capture.mode === 'partial') {
        // Mode A: extract and delete within a single contenteditable block.
        // cloneContents + deleteContents are reliable inside one contenteditable.
        const fragment = capture.range.cloneContents();
        const wrapper = document.createElement('div');
        wrapper.appendChild(fragment);
        extractedHtml = wrapper.innerHTML.trim();
        if (!extractedHtml) {
          extractedHtml = `<p>${capture.range.toString().trim()}</p>`;
        }
        capture.range.deleteContents();
        window.getSelection()?.removeAllRanges();
      } else {
        // Mode B/C: extract whole blocks via EditorJS save, then delete them.
        const outputData = await editor.save();
        const selectedBlocks = capture.blockIndices
          .filter(i => i >= 0 && i < outputData.blocks.length)
          .map(i => outputData.blocks[i]);
        if (selectedBlocks.length === 0) throw new Error('No blocks found');
        extractedHtml = editorJSToHtml({ ...outputData, blocks: selectedBlocks });
        // Delete from highest index to lowest to preserve indices
        const sorted = [...capture.blockIndices].sort((a, b) => b - a);
        for (const idx of sorted) {
          editor.blocks.delete(idx);
        }
      }

      if (!extractedHtml.trim()) throw new Error('Empty content');

      // Save editor state after mutations and propagate
      const remaining = await editor.save();
      const remainingHtml = editorJSToHtml(remaining);
      setCurrentValue(remainingHtml);
      onChange(remainingHtml);
      createStepCallbackRef.current?.(extractedHtml);
    } catch {
      toast({
        title: "Conversion échouée",
        description: "Le contenu n'a pas pu être déplacé vers une nouvelle étape.",
        variant: "destructive",
      });
    } finally {
      captureRef.current = null;
      setIsProcessing(false);
    }
  }, [contextMenu, isProcessing, onChange, toast]);

  useEffect(() => {
    if (contextMenu) {
      const dismiss = () => {
        setContextMenu(null);
        captureRef.current = null;
      };
      document.addEventListener('mousedown', dismiss);
      document.addEventListener('scroll', dismiss, true);
      return () => {
        document.removeEventListener('mousedown', dismiss);
        document.removeEventListener('scroll', dismiss, true);
      };
    }
  }, [contextMenu]);

  useEffect(() => {
    if (shouldRenderStatic || readOnly) return;
    const container = holderRef.current;
    if (!container) return;
    container.addEventListener('contextmenu', handleContextMenu, true);
    return () => container.removeEventListener('contextmenu', handleContextMenu, true);
  }, [shouldRenderStatic, readOnly, handleContextMenu]);

  useEffect(() => {
    if (shouldRenderStatic) return; // Skip initializing EditorJS when we render static HTML
    if (!holderRef.current || editorRef.current) return;
    let initialData: OutputData;
    if (value && isEditorJSData(value)) {
      try { initialData = JSON.parse(value); } catch { initialData = htmlToEditorJS(''); }
    } else {
      initialData = htmlToEditorJS(value || '');
    }
    const editor = new EditorJS({
      holder: holderRef.current,
      data: initialData,
      readOnly,
      placeholder,
      minHeight: 120,
      autofocus: false,
      logLevel: 'ERROR' as any,
      tools: {
        header: { class: Header, config: { levels: [1,2,3], defaultLevel: 1 } },
        list: { class: List, inlineToolbar: true, config: { defaultStyle: 'unordered' } },
        table: { class: Table, inlineToolbar: true },
        image: SimpleImageTool,
        inlineCode: { class: InlineCode, shortcut: 'CMD+SHIFT+M' },
        marker: { class: Marker, shortcut: 'CMD+SHIFT+H' },
        convertToStep: {
          class: ConvertToStepTune as any,
          config: { handlerRef: convertHandlerRef },
        },
      },
      tunes: ['convertToStep'],
      onChange: async () => {
        if (!readOnly && editorRef.current) {
          try {
            const outputData = await editorRef.current.save();
            const htmlContent = editorJSToHtml(outputData);
            setCurrentValue(htmlContent);
            onChange(htmlContent);
          } catch {}
        }
      },
      onReady: () => setIsReady(true),
      onFocus: () => handleEditorFocus(),
    });
    editorRef.current = editor;
    return () => {
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch {}
        editorRef.current = null;
      }
      setIsReady(false);
    };
  }, [readOnly, placeholder, shouldRenderStatic]);

  useEffect(() => {
    if (shouldRenderStatic) return;
    if (!isReady || !editorRef.current || value === currentValue) return;
    const updateContent = async () => {
      try {
        let newData: OutputData;
        if (value && isEditorJSData(value)) newData = JSON.parse(value);
        else newData = htmlToEditorJS(value);
        await editorRef.current!.blocks.clear();
        await editorRef.current!.blocks.render(newData);
        setCurrentValue(value);
      } catch {}
    };
    updateContent();
  }, [value, isReady, currentValue, shouldRenderStatic]);

  // Intercept EditorJS "Move up"/"Move down" clicks at block boundaries
  // to enable cross-step block movement
  useEffect(() => {
    if (shouldRenderStatic || readOnly || !onMoveBlockOut) return;
    const container = holderRef.current;
    if (!container) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const moveUpBtn = target.closest('[data-item-name="move-up"]');
      const moveDownBtn = target.closest('[data-item-name="move-down"]');
      if (!moveUpBtn && !moveDownBtn) return;
      if (!editorRef.current) return;

      const currentIndex = editorRef.current.blocks.getCurrentBlockIndex();
      const blocksCount = editorRef.current.blocks.getBlocksCount();
      const isAtTop = currentIndex === 0 && !!moveUpBtn;
      const isAtBottom = currentIndex === blocksCount - 1 && !!moveDownBtn;
      if (!isAtTop && !isAtBottom) return;

      // Prevent EditorJS from processing this click
      e.stopPropagation();
      e.preventDefault();

      const direction: 'up' | 'down' = isAtTop ? 'up' : 'down';

      editorRef.current.save().then((outputData) => {
        if (!outputData.blocks[currentIndex] || !editorRef.current) return;
        const blockData = outputData.blocks[currentIndex];
        const remainingBlocks = outputData.blocks.filter((_, i) => i !== currentIndex);
        const remainingHtml = editorJSToHtml({ ...outputData, blocks: remainingBlocks });

        // Remove the block from the current editor
        editorRef.current!.blocks.delete(currentIndex);
        setCurrentValue(remainingHtml);

        onMoveBlockOut(direction, { type: blockData.type, data: blockData.data }, remainingHtml);
      });
    };

    // Use capture phase to run before EditorJS's own handler
    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, [shouldRenderStatic, readOnly, onMoveBlockOut]);

  // Convert plain text with \n to proper HTML for display.
  // Handles mixed content where plain text with \n is interspersed
  // with HTML block elements (<ul>, <ol>, <table>, etc.).
  const convertLineBreaks = (text: string): string => {
    if (!text) return text;

    // If it already has <br> tags, don't double-convert
    if (text.includes('<br')) {
      let cleaned = text.replace(/^(<br\s*\/?>)+/gi, '');
      cleaned = cleaned.replace(/(<br\s*\/?>)+$/gi, '');
      return cleaned;
    }

    // Check if content is fully structured HTML (e.g., from EditorJS output)
    const isFullyStructuredHtml = /^\s*<(p|ul|ol|h[1-6]|table|div)[\s>]/i.test(text);

    if (isFullyStructuredHtml) {
      return text.replace(/>\s+</g, '><');
    }

    // For mixed content or plain text: split at block-level HTML boundaries
    // so that plain text segments get wrapped in <p> without nesting block
    // elements inside <p> (which causes browsers to auto-close the <p> and
    // lose <br> line breaks in the orphaned text that follows).
    const blockTagRe = /(<(?:ul|ol|table|div|blockquote|h[1-6])[\s>][\s\S]*?<\/(?:ul|ol|table|div|blockquote|h[1-6])>)/gi;

    const segments = text.split(blockTagRe);

    const processed = segments.map(segment => {
      // If this segment is a block-level HTML element, return as-is
      if (/^\s*<(ul|ol|table|div|blockquote|h[1-6])[\s>]/i.test(segment)) {
        // Clean up stray \n inside block elements (between tags)
        return segment.replace(/\n/g, '');
      }

      // Plain-text segment: wrap each \n-delimited line in its own <p>.
      // Using separate <p> tags rather than <br> inside one <p> ensures
      // reliable rendering across browsers and CSS frameworks (prose).
      const lines = segment.split(/\n/);

      return lines.map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '';

        // If line is already a block element, return as-is
        if (/^<(p|ul|ol|h[1-6]|table|div|blockquote)[\s>]/i.test(trimmed)) {
          return trimmed;
        }

        return `<p>${trimmed}</p>`;
      }).filter(Boolean).join('');
    }).filter(Boolean);

    return processed.join('\n');
  };

  // Sign image URLs when in static mode
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!shouldRenderStatic) {
        setSignedStatic("");
        return;
      }
      const processedValue = convertLineBreaks(value || "");
      const out = await signImageUrlsInHtml(processedValue);
      if (!cancelled) setSignedStatic(out);
    })();
    return () => { cancelled = true };
  }, [shouldRenderStatic, value]);

  if (shouldRenderStatic) {
    let displayValue: string;
    if (signedStatic) {
      displayValue = signedStatic;
    } else {
      const processed = convertLineBreaks(value || "");
      // Strip unsigned image sources so browser doesn't request them (nginx returns 403)
      displayValue = /src=["'][^"']*\/outputs\/extracted_images\//i.test(processed)
        ? processed.replace(
            /(<img\s+[^>]*?)src=["']\/outputs\/extracted_images\/[^"']*["']/gi,
            '$1src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"'
          )
        : processed;
    }
    return (
      <div className={cn("wysiwyg-editor", className)} data-phase={phaseIndex} data-step={stepIndex}>
        <div className="prose prose-sm max-w-none document-content" dangerouslySetInnerHTML={{ __html: displayValue }} />
      </div>
    );
  }

  return (
      <div className={cn("wysiwyg-editor", className)} data-phase={phaseIndex} data-step={stepIndex}>
        <div ref={holderRef} className="editor-js-container" />
        {contextMenu && ReactDOM.createPortal(
          <div
            className="fixed z-50 min-w-[12rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
              disabled={isProcessing}
              onClick={handleContextMenuAction}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="12" y1="18" x2="12" y2="12"/>
                <line x1="9" y1="15" x2="15" y2="15"/>
              </svg>
              Convertir en nouvelle étape
            </button>
          </div>,
          document.body
        )}
      </div>
  );
}


